import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdCheckCircle,
  MdOutlineError,
} from 'react-icons/md';
import OrderProgress from './orderProgress';
import CartItem from '../../../cart/components/cartItem';
import CheckoutTotal from '../../../checkout/components/total';
import { formatProductForCartFromOrder } from '../../../../services/cartService';
import MainButton from '../../../../components/buttons/main';
import PaymentRating from '../../../payments/paymentRating';
import PaymentReceipt from '../../../payments/receiptDownload';

const CustomOrder = ({ order, table, restaurant, canRate, onUpdateRating }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [detailsVisible, setDetailsVisible] = useState(false);

  if (!order) {
    return null;
  }

  const cart = useMemo(() => {
    if (!order.products || order.products.length === 0) {
      return [];
    }

    return order.products.map((product) => formatProductForCartFromOrder({ product }));
  }, [order.products]);

  const showToPayAtBar = () => {
    if (
      (!order.payments || order.payments.length === 0) &&
      [
        '6655f6f819551c534379b634',
        '66430116d8304dd333c986c8',
        '665f315c19551c5343844cbe',
        '64ef42bd69364cb8ac09a8ee',
        '6525a6f6395dd52741185032',
      ].includes(restaurant.id)
    ) {
      if ((order.price || {}).leftToPay < 0.01) {
        return null;
      }

      return (
        <p className="font-primaryMedium text-md text-primaryOrange flex items-center -mt-4 mb-5">
          <MdOutlineError className="inline-block mr-1" /> {t('order.pleasePayAtCashier')}
        </p>
      );
    }

    return null;
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between mb-5">
        <p className="font-primaryMedium text-2xl text-primary mr-2">
          {t('header.order')} {order.externalNumber || order.number}
        </p>
        {((order.payments && order.payments.length > 0) ||
          (order.price || {}).leftToPay < 0.01) && (
          <p className="font-primaryMedium text-lg text-success2 flex items-center">
            {t('order.productPaid')} <MdCheckCircle className="inline-block ml-1" />
          </p>
        )}
      </div>
      {showToPayAtBar()}
      <OrderProgress isCompleted={order.isCompleted} isInProgress={order.isInProgress} />
      <p className="mt-6 font-primaryMedium text-primary text-base">
        {t('order.bringToTableExplanation')}
      </p>
      <p className="mt-3 font-primaryMedium text-primary text-base">
        {t('common.tableNo')} {table.number}
      </p>
      <button
        type="button"
        onClick={() => setDetailsVisible((prev) => !prev)}
        className="mt-3 mb-5 font-primaryMedium text-sm text-bland underline"
      >
        {t('buttons.seeOrderDetails')}{' '}
        {detailsVisible ? (
          <MdKeyboardArrowUp className="inline" />
        ) : (
          <MdKeyboardArrowDown className="inline" />
        )}
      </button>
      {detailsVisible && (
        <div>
          {cart &&
            cart.map((item) => <CartItem key={`${item.productId}-${item.index}`} product={item} />)}
          <div className="pt-2">
            <CheckoutTotal total={(order.price || {}).total || 0} noDivider />
          </div>
        </div>
      )}
      <div className="mt-10 mb-2">
        <MainButton text={t('buttons.viewMenu')} onClick={() => navigate('/menu')} />
      </div>
      {canRate && (
        <PaymentRating
          orderRating={order.orderRating}
          payment={order.payments && order.payments.length > 0 ? order.payments[0] : null}
          restaurantName={restaurant.name}
          onUpdateRating={(newRating, newComment) =>
            onUpdateRating(order.id, newRating, newComment)
          }
          reviewLink={restaurant.reviewLink}
        />
      )}
      <PaymentReceipt
        payment={order.payments && order.payments.length > 0 ? order.payments[0] : null}
      />
    </div>
  );
};

export default CustomOrder;
